import { friendlyDbError } from '../utils/friendlyDbError';
import { create } from 'zustand';
import { supabase } from '../supabase';
import { eventBus } from '../events/eventBus';
import type { LeadNote, NoteAttachment } from '../types';

export interface EmailNotePayload {
  gmailThreadId: string;
  subject: string;
  fromEmail: string;
  fromName?: string | null;
  snippet?: string | null;
  messageCount: number;
  pipelineId?: string;
}

// Escape HTML per inserire testo dell'email nel content della nota in sicurezza
function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

// Emette l'evento workflow corretto (lead vs client) dopo la creazione di una nota.
// Estratto da createNote per riuso con createEmailNote.
async function emitNoteCreatedEvent(leadId: string, noteId: string, noteContent: string) {
  try {
    const { data: entityData } = await supabase
      .from('clients')
      .select('pipeline_id, business_name, stato_cliente')
      .eq('id', leadId)
      .single();

    if (!entityData) return;

    const { data: stateData } = await supabase
      .from('client_states')
      .select('is_lead')
      .eq('id', entityData.stato_cliente)
      .maybeSingle();

    if (stateData?.is_lead) {
      eventBus.emitEvent({
        type: 'lead:note_created',
        payload: {
          leadId,
          noteId,
          pipelineId: entityData.pipeline_id,
          statoCliente: entityData.stato_cliente,
          businessName: entityData.business_name,
          noteContent,
        },
      });
    } else {
      eventBus.emitEvent({
        type: 'client:note_created',
        payload: {
          clientId: leadId,
          noteId,
          businessName: entityData.business_name,
          noteContent,
        },
      });
    }
  } catch (eventError) {
    console.error('Error emitting event:', eventError);
    // Non fallire la creazione della nota se l'evento fallisce
  }
}

interface LeadNoteStore {
  notes: LeadNote[];
  loading: boolean;
  error: string | null;
  fetchNotesByLeadId: (leadId: string, options?: { pipelineId?: string; showAll?: boolean; organizationId?: string; isCurrent?: () => boolean }) => Promise<void>;
  createNote: (leadId: string, content: string, pipelineId?: string) => Promise<LeadNote | null>;
  createEmailNote: (target: { leadId?: string; dealId?: string }, payload: EmailNotePayload) => Promise<LeadNote | null>;
  updateNote: (noteId: string, content: string) => Promise<LeadNote | null>;
  deleteNote: (noteId: string) => Promise<boolean>;
  clearNotes: () => void;
  uploadNoteAttachment: (noteId: string, file: File) => Promise<NoteAttachment | null>;
  deleteNoteAttachment: (attachmentId: string) => Promise<boolean>;
}

export const useLeadNoteStore = create<LeadNoteStore>((set, get) => ({
  notes: [],
  loading: false,
  error: null,

  fetchNotesByLeadId: async (leadId: string, options?: { pipelineId?: string; showAll?: boolean; organizationId?: string; isCurrent?: () => boolean }) => {
    // Don't set loading=true to avoid flash of empty state when re-fetching
    // Keep existing notes visible while fetching updated ones
    set({ error: null });
    try {
      // Fetch ALL notes for this lead (filtering by pipeline is done client-side in LeadNotesSection)
      let notesQuery = supabase.from('deal_notes').select('*').eq('lead_id', leadId);
      if (options?.organizationId) notesQuery = notesQuery.eq('organization_id', options.organizationId);
      const { data, error } = await notesQuery.order('created_at', { ascending: false }).limit(100);
      if (options?.isCurrent && !options.isCurrent()) return;

     /*  console.log(`[LEAD_NOTE_STORE] 📊 Query result:`, {
        error: error?.message,
        dataCount: data?.length,
        rawData: data
      }); */

      if (error) throw error;

      // Get unique user IDs from notes
      const userIds = [...new Set((data || []).map(note => note.user_id).filter(Boolean))];

      // Fetch author information for all users in a single query
      let authorsMap: Record<string, any> = {};
      if (userIds.length > 0) {
        let authorsQuery = supabase.from('user_profiles').select('id, full_name, email, avatar_url, profile_image_url').in('id', userIds);
        if (options?.organizationId) authorsQuery = authorsQuery.eq('organization_id', options.organizationId);
        const { data: authorsData, error: authorsError } = await authorsQuery;

        if (!authorsError && authorsData) {
          authorsMap = authorsData.reduce((acc, author) => {
            acc[author.id] = author;
            return acc;
          }, {} as Record<string, any>);
        }
      }

      // Fetch attachments for all notes
      const noteIds = (data || []).map(note => note.id);
      let attachmentsMap: Record<string, NoteAttachment[]> = {};
      if (noteIds.length > 0) {
        let attachmentsQuery = supabase.from('note_attachments').select('*').in('note_id', noteIds);
        if (options?.organizationId) attachmentsQuery = attachmentsQuery.eq('organization_id', options.organizationId);
        const { data: attachmentsData, error: attachmentsError } = await attachmentsQuery.order('created_at', { ascending: true });

        if (!attachmentsError && attachmentsData) {
          attachmentsMap = attachmentsData.reduce((acc, attachment) => {
            if (!acc[attachment.note_id]) {
              acc[attachment.note_id] = [];
            }
            acc[attachment.note_id].push(attachment);
            return acc;
          }, {} as Record<string, NoteAttachment[]>);
        }
      }

      // Transform the data to match the expected format
      const notesWithAuthors = (data || []).map(note => ({
        ...note,
        author: !note.user_id
          ? { id: 'system', email: 'system@flusia.it', full_name: 'Flusia', profile_image_url: 'https://flusia.it/favicon.ico' }
          : authorsMap[note.user_id] || {
              id: note.user_id,
              email: 'Unknown User',
              full_name: 'Unknown User',
              profile_image_url: null
            },
        attachments: attachmentsMap[note.id] || []
      }));

      console.log(`[LEAD_NOTE_STORE] ✅ Fetched ${notesWithAuthors.length} notes for lead ${leadId}`);
      console.log(`[LEAD_NOTE_STORE] 📝 Notes data:`, notesWithAuthors);
      if (options?.isCurrent && !options.isCurrent()) return;
      set({ notes: notesWithAuthors });
    } catch (error) {
/*       console.error('[LEAD_NOTE_STORE] ❌ Error fetching notes:', error);
 */      if (options?.isCurrent && !options.isCurrent()) return;
      set({ error: friendlyDbError(error) });
    }
    // Don't set loading to false - we never set it to true
  },

  createNote: async (leadId: string, content: string, pipelineId?: string) => {
    try {
      console.log(`[LEAD_STORE] Creating note for lead: ${leadId}, pipeline: ${pipelineId || 'none'}`);

      // Get current user
      const { data: userData, error: userError } = await supabase.auth.getUser();
      if (userError) throw userError;

      // Get user's organization_id and profile info
      const { data: profileData, error: profileError } = await supabase
        .from('user_profiles')
        .select('organization_id, full_name, email, avatar_url, profile_image_url')
        .eq('id', userData.user.id)
        .single();

      if (profileError) throw profileError;

      // Se non è stato passato un pipelineId, prova a prenderlo dal client
      let effectivePipelineId = pipelineId;
      if (!effectivePipelineId) {
        const { data: clientData } = await supabase
          .from('clients')
          .select('pipeline_id')
          .eq('id', leadId)
          .single();
        effectivePipelineId = clientData?.pipeline_id || undefined;
      }

      const insertData: any = {
        lead_id: leadId,
        user_id: userData.user.id,
        content: content,
        organization_id: profileData.organization_id
      };
      if (effectivePipelineId) {
        insertData.pipeline_id = effectivePipelineId;
      }

      console.log('[LEAD_NOTE_STORE] 📝 Creating note with pipeline_id:', effectivePipelineId, 'insertData:', JSON.stringify(insertData));

      const { data, error } = await supabase
        .from('deal_notes')
        .insert(insertData)
        .select('*')
        .single();

      console.log('[LEAD_NOTE_STORE] 📝 Note created result:', { pipeline_id: data?.pipeline_id, error: error?.message });

      if (error) throw error;

      // Add author information from user_profiles (not auth metadata)
      const newNote = {
        ...data,
        author: {
          id: userData.user.id,
          email: profileData.email || userData.user.email,
          full_name: profileData.full_name || 'User',
          avatar_url: (profileData as any).avatar_url,
          profile_image_url: profileData.profile_image_url
        }
      };

      const notes = get().notes;
      set({ notes: [newNote, ...notes] });

      // Emit event for workflow triggers
      await emitNoteCreatedEvent(leadId, data.id, content);

      console.log(`[LEAD_STORE] Note created successfully for lead: ${leadId}`);
      return newNote;
    } catch (error) {
      console.error('[LEAD_STORE] Error creating note:', error);
      set({ error: friendlyDbError(error) });
      return null;
    }
  },

  createEmailNote: async (target: { leadId?: string; dealId?: string }, payload: EmailNotePayload) => {
    try {
      const { leadId, dealId } = target;
      if (!leadId && !dealId) throw new Error('createEmailNote: leadId o dealId richiesto');

      const { data: userData, error: userError } = await supabase.auth.getUser();
      if (userError) throw userError;

      const { data: profileData, error: profileError } = await supabase
        .from('user_profiles')
        .select('organization_id, full_name, email, avatar_url, profile_image_url')
        .eq('id', userData.user.id)
        .single();

      if (profileError) throw profileError;

      // Eredita la pipeline dal client se non passata (solo per note di lead)
      let effectivePipelineId = payload.pipelineId;
      if (leadId && !effectivePipelineId) {
        const { data: clientData } = await supabase
          .from('clients')
          .select('pipeline_id')
          .eq('id', leadId)
          .single();
        effectivePipelineId = clientData?.pipeline_id || undefined;
      }

      const sender = payload.fromName?.trim() || payload.fromEmail;
      const gmailUrl = `https://mail.google.com/mail/u/0/#all/${payload.gmailThreadId}`;
      const countLabel = payload.messageCount === 1 ? '1 messaggio' : `${payload.messageCount} messaggi`;

      // Snapshot HTML leggibile (renderizzato anche fuori dalla EmailNoteCard come fallback)
      const content =
        `<p><strong>${escapeHtml(payload.subject || '(Nessun oggetto)')}</strong></p>` +
        `<p>Email da: ${escapeHtml(sender)} &lt;${escapeHtml(payload.fromEmail)}&gt; · ${countLabel}</p>` +
        (payload.snippet ? `<p>${escapeHtml(payload.snippet)}</p>` : '') +
        `<p><a href="${gmailUrl}" target="_blank" rel="noopener noreferrer">Apri in Gmail</a></p>`;

      const insertData: any = {
        ...(leadId ? { lead_id: leadId } : { deal_id: dealId }),
        user_id: userData.user.id,
        content,
        note_type: 'email',
        organization_id: profileData.organization_id,
        metadata: {
          gmail_thread_id: payload.gmailThreadId,
          from_email: payload.fromEmail,
          from_name: payload.fromName || null,
          subject: payload.subject || null,
          message_count: payload.messageCount,
          snippet: payload.snippet || null,
        },
      };
      // pipeline_id valido solo per note di lead
      if (leadId && effectivePipelineId) {
        insertData.pipeline_id = effectivePipelineId;
      }

      const { data, error } = await supabase
        .from('deal_notes')
        .insert(insertData)
        .select('*')
        .single();

      if (error) throw error;

      const newNote = {
        ...data,
        author: {
          id: userData.user.id,
          email: profileData.email || userData.user.email,
          full_name: profileData.full_name || 'User',
          avatar_url: (profileData as any).avatar_url,
          profile_image_url: profileData.profile_image_url,
        },
      };

      // Aggiorna lo store note solo se stiamo guardando lo stesso lead (le note deal
      // hanno uno store separato nel componente DealNotesSection).
      if (leadId) set({ notes: [newNote, ...get().notes] });

      // NB: di proposito NON emettiamo lead:note_created qui. Una nota-snapshot di
      // collegamento email non e` una nota scritta da un umano e non deve far scattare
      // le automazioni "alla creazione nota" (eviterebbe side-effect indesiderati).

      return newNote;
    } catch (error) {
      console.error('[LEAD_STORE] Error creating email note:', error);
      set({ error: friendlyDbError(error) });
      return null;
    }
  },

  updateNote: async (noteId: string, content: string) => {
    try {
      console.log(`[LEAD_STORE] Updating note: ${noteId}`);
      
      // Get current note content to create version
      const currentNote = get().notes.find(note => note.id === noteId);
      if (currentNote) {
        // Create version entry with current content before updating
        try {
          // Get the next version number
          const { data: versionData, error: versionCountError } = await supabase
            .from('deal_note_versions')
            .select('version_number')
            .eq('note_id', noteId)
            .order('version_number', { ascending: false })
            .limit(1);

          if (versionCountError) throw versionCountError;

          const nextVersionNumber = versionData && versionData.length > 0 ?
            versionData[0].version_number + 1 : 1;

          // Create version entry
          const { error: versionError } = await supabase
            .from('deal_note_versions')
            .insert({
              note_id: noteId,
              content: currentNote.content,
              user_id: currentNote.user_id,
              version_number: nextVersionNumber
            });

          if (versionError) {
            console.warn('Failed to create note version:', versionError);
          }
        } catch (versionError) {
          console.warn('Failed to create note version:', versionError);
        }
      }
      
      const { data, error} = await supabase
        .from('deal_notes')
        .update({
          content,
          updated_at: new Date().toISOString(),
          is_edited: true,
          edit_count: (currentNote?.edit_count || 0) + 1
        })
        .eq('id', noteId)
        .select('*')
        .single();

      if (error) throw error;

      // Preserve author info AND attachments
      const updatedNote = {
        ...data,
        author: currentNote?.author || {
          id: data.user_id,
          email: 'Unknown User',
          full_name: 'Unknown User',
          profile_image_url: null
        },
        attachments: currentNote?.attachments || []
      };

      const notes = get().notes.map(note => 
        note.id === noteId ? updatedNote : note
      );
      set({ notes });
      
      console.log(`[LEAD_STORE] Note updated successfully: ${noteId}`);
      return updatedNote;
    } catch (error) {
      console.error('[LEAD_STORE] Error updating note:', error);
      set({ error: friendlyDbError(error) });
      return null;
    }
  },

  deleteNote: async (noteId: string) => {
    try {
      const { error } = await supabase
        .from('deal_notes')
        .delete()
        .eq('id', noteId);

      if (error) throw error;

      const notes = get().notes.filter(note => note.id !== noteId);
      set({ notes });
      return true;
    } catch (error) {
      set({ error: friendlyDbError(error) });
      return false;
    }
  },

  clearNotes: () => {
    set({ notes: [] });
  },

  uploadNoteAttachment: async (noteId: string, file: File) => {
    try {
/*       console.log(`[LEAD_NOTE_STORE] 📎 Uploading attachment for note: ${noteId}`);
 */
      // Upload file to storage (sanitize filename for iPhone/mobile compatibility)
      const safeName = file.name.replace(/[^a-zA-Z0-9._-]/g, '_');
      const fileName = `${Date.now()}-${safeName}`;
      const filePath = `notes/${noteId}/${fileName}`;

      const { error: uploadError } = await supabase.storage
        .from('documents')
        .upload(filePath, file);

      if (uploadError) throw uploadError;

      // Get the public URL
      const { data: urlData } = supabase.storage
        .from('documents')
        .getPublicUrl(filePath);

      if (!urlData.publicUrl) {
        throw new Error('Failed to get public URL');
      }

      // Get current user and organization
      const { data: userData, error: userError } = await supabase.auth.getUser();
      if (userError) throw userError;

      const { data: profileData, error: profileError } = await supabase
        .from('user_profiles')
        .select('organization_id')
        .eq('id', userData.user.id)
        .single();

      if (profileError) throw profileError;

      // Create attachment record
      const { data: attachment, error: dbError } = await supabase
        .from('note_attachments')
        .insert({
          note_id: noteId,
          name: file.name,
          url: urlData.publicUrl,
          type: file.type,
          size: file.size,
          created_by: userData.user.id,
          organization_id: profileData.organization_id
        })
        .select()
        .single();

      if (dbError) throw dbError;

      // Update local state
      const notes = get().notes.map(note => {
        if (note.id === noteId) {
          return {
            ...note,
            attachments: [...(note.attachments || []), attachment]
          };
        }
        return note;
      });

      set({ notes });
/*       console.log(`[LEAD_NOTE_STORE] ✅ Attachment uploaded successfully`);
 */      return attachment;
    } catch (error) {
/*       console.error('[LEAD_NOTE_STORE] ❌ Error uploading attachment:', error);
 */      set({ error: friendlyDbError(error) });
      return null;
    }
  },

  deleteNoteAttachment: async (attachmentId: string) => {
    try {
/*       console.log(`[LEAD_NOTE_STORE] 🗑️ Deleting attachment: ${attachmentId}`);
 */
      // Get the attachment to get the file path
      const { data: attachment, error: getError } = await supabase
        .from('note_attachments')
        .select('url, note_id')
        .eq('id', attachmentId)
        .single();

      if (getError) throw getError;

      // Extract the file path from the URL
      const url = new URL(attachment.url);
      const pathParts = url.pathname.split('/');
      const filePath = pathParts.slice(-3).join('/'); // notes/{noteId}/{filename}

      // Delete from storage
      const { error: storageError } = await supabase.storage
        .from('documents')
        .remove([filePath]);

      if (storageError) {
        console.warn('Failed to delete from storage:', storageError);
        // Continue anyway, we still want to delete the DB record
      }

      // Delete from database
      const { error: dbError } = await supabase
        .from('note_attachments')
        .delete()
        .eq('id', attachmentId);

      if (dbError) throw dbError;

      // Update local state
      const notes = get().notes.map(note => {
        if (note.id === attachment.note_id) {
          return {
            ...note,
            attachments: (note.attachments || []).filter(a => a.id !== attachmentId)
          };
        }
        return note;
      });

      set({ notes });
/*       console.log(`[LEAD_NOTE_STORE] ✅ Attachment deleted successfully`);
 */      return true;
    } catch (error) {
/*       console.error('[LEAD_NOTE_STORE] ❌ Error deleting attachment:', error);
 */      set({ error: friendlyDbError(error) });
      return false;
    }
  }
}));